import type { IconNames } from "@/v2-ui/icon/icon.types"
import type { ActionMenuItemVariant } from "@/v2-backoffice-client/components/actionMenu"
import {
  USER_STATUS_ACTIVE,
  USER_STATUS_BLOCKED,
  USER_STATUS_REMOVED,
} from "@/v2-backoffice-client/user/status/userStatus.constants"

// The statuses an operator can move a user to. Provisioned is absent on
// purpose: it's the pre-sign-in state the platform assigns, not something
// anyone sets by hand.
export const USER_STATUS_UPDATE_CODES = [
  USER_STATUS_ACTIVE,
  USER_STATUS_BLOCKED,
  USER_STATUS_REMOVED,
] as const

export type UserStatusUpdateCode = (typeof USER_STATUS_UPDATE_CODES)[number]

type UserStatusUpdateAction = {
  /** Toolbar and bulk-toolbar label. */
  label: string
  /** Action-menu label, which names the object it acts on. */
  menuLabel: string
  iconSrc: IconNames
  /** Narrowed to the variants the action menu accepts as well as Button. */
  variant: ActionMenuItemVariant
  /** Tooltip: what the action does. */
  content: string
  /** Confirmation copy for a single user. */
  confirmContent: string
}

// One descriptor per target status, shared by every surface that offers the
// action: the action menu (UserStatusUpdateActions), the view toolbar
// (UserStatusUpdateButton) and the bulk surfaces. Keeping the copy here is
// what stops "Block" meaning one thing in the menu and another in a toolbar.
export const USER_STATUS_UPDATE_ACTIONS: Record<
  UserStatusUpdateCode,
  UserStatusUpdateAction
> = {
  [USER_STATUS_ACTIVE]: {
    label: "Activate",
    menuLabel: "Activate User",
    iconSrc: "check-circle",
    variant: "success",
    content: "Activate this account so the user can sign in again.",
    confirmContent: "Are you sure you want to activate this user?",
  },
  [USER_STATUS_BLOCKED]: {
    label: "Block",
    menuLabel: "Block User",
    iconSrc: "lock",
    variant: "warning",
    content:
      "Block this account so the user can't sign in until it's reactivated.",
    confirmContent:
      "Are you sure you want to block this user? They will not be able to sign in.",
  },
  [USER_STATUS_REMOVED]: {
    label: "Remove",
    menuLabel: "Remove User",
    iconSrc: "trash",
    variant: "destructive",
    content: "Permanently remove this user account.",
    confirmContent:
      "Are you sure you want to remove this user? This is a soft delete and can be reversed.",
  },
}
